import React from "react";
import { NavLogo } from "../NavLogo";
import { DownloadApp } from "./DownloadApp";
import {
  AboutLinks,
  LegalLinks,
} from "@/features/account/components/about/Links";
import { FooterLink } from "./FooterLink";
import { ReactComponent as Logo } from "assets/logos/logo.svg";
import { useIsClient } from "@/hooks/useIsClient";
import { ResponsibleGambling } from "./ResponsibleGambling";
import { SACodeOfPractice } from "./SACodeOfPractice";
import { useCustomerLicence } from "@/hooks/firestore/user/useProfile";
import {
  type CustomerLicence,
  CustomerLicenceOptions,
} from "@/features/auth/stores/authStore";
import { SocialIcons } from "./SocialIcons";
import { SkeletonParagraph } from "@picklebet/ui";
import { IsleOfManFooterContent } from "./IsleOfManFooterContent";

import * as styles from "./Footer.module.scss";
import cx from "classnames";

const InfoContent = ({ licence }: { licence: CustomerLicence | null }) => {
  const isIsleOfMan = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  if (!licence) {
    return <SkeletonParagraph />;
  }

  if (isIsleOfMan) {
    return <IsleOfManFooterContent />;
  }

  return (
    <div className={`${styles.section} ${styles.info}`}>
      <div>
        Licensed and regulated by the Northern Territory Wagering Commission.
      </div>
      <div className={styles.showMinWidthDesktop}>
        <SACodeOfPractice />
      </div>
      <div>
        Puntaa Pty Ltd trading as Picklebet. Unit 1, 48-50 Smith Street Darwin
        NT 0800.
      </div>
      <ResponsibleGambling />
    </div>
  );
};

const InfoContentCompact = ({
  licence,
}: {
  licence: CustomerLicence | null;
}) => {
  const isIsleOfMan = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  if (!licence) {
    return (
      <div className={styles.compactFooterSection}>
        <SkeletonParagraph />
      </div>
    );
  }

  if (isIsleOfMan) {
    return (
      <div className={styles.compactFooterSection}>
        <IsleOfManFooterContent />
      </div>
    );
  }

  return null;
};

const Footer = () => {
  const isClient = useIsClient();
  const licence = useCustomerLicence();
  const isNTRWC = licence === CustomerLicenceOptions.NTRWC;
  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  if (!isClient) {
    return null;
  }

  return (
    <div
      className={cx(styles.container, {
        [styles.isIOM]: isIOM,
      })}
    >
      <div className={styles.expanded}>
        <div className={styles.section}>
          <NavLogo />
          <DownloadApp />
        </div>
        <div className={styles.section}>
          <h3>Info</h3>
          <InfoContent licence={licence} />
        </div>
        <div className={styles.section}>
          <h3>Legal</h3>
          {LegalLinks.map((link) => {
            if (link.isVisible && !link.isVisible(licence)) {
              return null;
            }
            return <FooterLink key={link.name} link={link} />;
          })}
        </div>
        <div className={styles.section}>
          <h3>Help</h3>
          {AboutLinks.map((link) => (
            <FooterLink key={link.name} link={link} />
          ))}
        </div>
      </div>
      <hr />
      <div className={styles.compact}>
        <div className={styles.logo}>
          <Logo />
        </div>
        <SocialIcons />
        <div className={styles.compactInfoContent}>
          <div className={styles.links}>
            <FooterLink
              link={{
                name: "Terms & Conditions",
                supportOptions: { labels: ["weblink-terms-and-conditions"] },
              }}
            />
            {isNTRWC && (
              <FooterLink
                link={{
                  name: "Responsible Gambling",
                  supportOptions: {
                    labels: ["weblink-responsible-gambling-policy"],
                  },
                }}
              />
            )}
          </div>

          {isNTRWC && (
            <div className={styles.sacop}>
              <SACodeOfPractice />
            </div>
          )}
        </div>
      </div>

      {isNTRWC && (
        <div className={styles.hideMinWidthDesktop}>
          <ResponsibleGambling />
        </div>
      )}

      {!isNTRWC && (
        <div className={styles.mobileTextContent}>
          <InfoContentCompact licence={licence} />
        </div>
      )}
    </div>
  );
};

export { Footer };
